import { useEffect, useRef } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { Menu, Settings, XIcon } from 'lucide-react'
import { Button } from '@/shared/ui'
import { useAuthStore } from '@/shared/session'
import { CONTENT_REPO } from '@/shared/config'
import { useSavedCharactersStore } from '@/entities/character'

import { useRollStore } from '@/entities/roll'
import { useSettingsStore } from '@/entities/settings'
import { useContentStore } from '@/entities/content'
import { usePartyStore } from '@/entities/party'

import { cn, prefersReducedMotion } from '@/shared/lib'
import { AuthButton } from '@/widgets/auth-button'
import { AnimationsPrompt } from '@/widgets/animations-prompt'

const FOOTER_LINKS = [
  { href: CONTENT_REPO, label: 'Content packs & example characters' },
  { href: `${CONTENT_REPO}/-/issues`, label: 'Report a bug or request a feature' },
]

/** `also`: other routes that belong to the item (a character or party opened from Faces is part of Faces). */
const NAV_ITEMS = [
  { path: '/',               label: 'Home',           also: [] },
  { path: '/faces',          label: 'Faces',          also: ['/create', '/character/', '/parties/', '/join/'] },
  { path: '/custom-content', label: 'Custom Content', also: [] },
]

export default function App() {
  const location = useLocation()
  // The View Transitions API itself ignores prefers-reduced-motion; this is the
  // one check that matters for it (the site's own, off-by-default animations
  // toggle is a different concern and would otherwise silence this for everyone).
  const withTransition = !prefersReducedMotion()

  const initialize = useAuthStore((s) => s.initialize)
  const loadFromCloud = useSavedCharactersStore((s) => s.loadFromCloud)
  const clearCloudIds = useSavedCharactersStore((s) => s.clearCloudIds)

  const drawerRef = useRef<HTMLDialogElement>(null)
  const closeDrawer = () => drawerRef.current?.close()
  const loadRollPrefsFromCloud = useRollStore((s) => s.loadRollPrefsFromCloud)
  const loadSettingsFromCloud = useSettingsStore((s) => s.loadSettingsFromCloud)
  const syncDueSubscriptions = useContentStore((s) => s.syncDueSubscriptions)
  const loadPartiesFromCloud = usePartyStore((s) => s.loadFromCloud)
  const clearParties = usePartyStore((s) => s.clear)
  const startPartyRealtime = usePartyStore((s) => s.startRealtime)
  const stopPartyRealtime = usePartyStore((s) => s.stopRealtime)

  // On app load, refresh any subscriptions whose last check is stale (the cooldown
  // keeps reopening the app from re-hitting every source). Manual checks bypass it.
  useEffect(() => {
    syncDueSubscriptions()
  }, [syncDueSubscriptions])

  useEffect(() => {
    const unsubAuth = initialize()
    const unsubStore = useAuthStore.subscribe((state, prev) => {
      if (state.user && !prev.user) {
        void loadFromCloud()
        void loadRollPrefsFromCloud()
        void loadSettingsFromCloud()
        void loadPartiesFromCloud()
        startPartyRealtime()
      } else if (!state.user && prev.user) {
        clearCloudIds()
        clearParties()
        stopPartyRealtime()
        // So a later sign-in waits for its own check rather than reusing this one's.
        useSettingsStore.setState({ cloudSettingsChecked: false })
      }
    })
    return () => { unsubAuth(); unsubStore(); stopPartyRealtime() }
  }, [initialize, loadFromCloud, clearCloudIds, loadRollPrefsFromCloud, loadSettingsFromCloud, loadPartiesFromCloud, clearParties, startPartyRealtime, stopPartyRealtime])

  function isActive(path: string, also: string[] = []) {
    return path === '/'
      ? location.pathname === '/'
      : [path, ...also].some((p) => location.pathname.startsWith(p))
  }

  return (
    <div className="flex min-h-screen flex-col">
      <AnimationsPrompt />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-md"
      >
        Skip to main content
      </a>
      {/* The bar (border, blur) spans the window; its content is held to 1200px, centered. */}
      <header className="sticky top-0 z-40 border-b bg-background print:hidden">
        <div className="mx-auto flex w-full max-w-[1200px] items-center gap-3 px-4 py-3">
          {/* Phones: the navigation collapses into a drawer opened from here. */}
          <Button
            variant="outline"
            size="icon"
            aria-label="Menu"
            aria-haspopup="dialog"
            className="sm:hidden pointer-coarse:size-11"
            onClick={() => drawerRef.current?.showModal()}
          >
            <Menu className="size-4" />
          </Button>
          <Link
            to="/"
            viewTransition={withTransition}
            className="flex items-center gap-2 rounded-sm text-lg font-semibold tracking-tight transition-colors hover:text-link"
          >
            {/* The logo is a mask filled with the text color, so it follows the theme
                and the link's hover color instead of the SVG's own (black) fill. */}
            <span
              aria-hidden="true"
              className="h-11 w-11 shrink-0 bg-current [mask-image:url(/logo.svg)] [mask-position:center] [mask-repeat:no-repeat] [mask-size:contain]"
            />
            {/* Icon-only branding; the name stays for screen readers. */}
            <span className="sr-only">Tome of Many Faces</span>
          </Link>
          <nav aria-label="Primary" className="flex items-center gap-1 max-sm:hidden">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                viewTransition={withTransition}
                aria-current={isActive(item.path, item.also) ? 'page' : undefined}
                className={cn(
                  'rounded-sm px-2 py-1.5 text-sm font-medium transition-colors hover:text-foreground',
                  isActive(item.path, item.also) ? 'text-foreground underline underline-offset-4' : 'text-muted-foreground',
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <AuthButton />
            <Link
              to="/settings"
              viewTransition={withTransition}
              aria-label="Settings"
              aria-current={isActive('/settings') ? 'page' : undefined}
              className={cn(
                'flex size-8 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground',
                isActive('/settings') && 'text-foreground',
              )}
            >
              <Settings className="size-4" />
            </Link>
          </div>
        </div>
        {/* Native <dialog> (not Base UI): the top layer makes the page inert without
            aria-hidden. Slides in from the left; a click on the backdrop closes it. */}
        <dialog
          ref={drawerRef}
          aria-label="Menu"
          onClick={(e) => { if (e.target === drawerRef.current) closeDrawer() }}
          className="fixed inset-y-0 left-0 m-0 h-dvh max-h-none w-72 max-w-[85vw] border-r bg-popover p-0 text-popover-foreground shadow-lg backdrop:bg-overlay/40 backdrop:backdrop-blur-xs sm:hidden anim:transition-[translate,overlay,display] anim:transition-discrete anim:duration-200 anim:ease-out anim:starting:open:-translate-x-full anim:not-open:-translate-x-full anim:backdrop:transition-[opacity,overlay,display] anim:backdrop:transition-discrete anim:backdrop:duration-200 anim:starting:open:backdrop:opacity-0 anim:not-open:backdrop:opacity-0"
        >
          <div className="flex h-full flex-col gap-4 p-4">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold tracking-tight">Tome of Many Faces</span>
              <Button variant="ghost" size="icon" aria-label="Close menu" className="pointer-coarse:size-11" onClick={closeDrawer}>
                <XIcon className="size-4" />
              </Button>
            </div>
            <nav aria-label="Primary" className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={closeDrawer}
                  viewTransition={withTransition}
                  aria-current={isActive(item.path, item.also) ? 'page' : undefined}
                  className={cn(
                    'rounded-sm px-3 py-2.5 text-base font-medium transition-colors hover:text-foreground',
                    isActive(item.path, item.also) ? 'text-foreground underline underline-offset-4' : 'text-muted-foreground',
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </dialog>
      </header>

      {/* Same outer box as the header's own max-w-[1200px]/px-4, plus the extra inset the
          logo + nav take there (icon + gap), so a page's content lines up with "Home" at
          every width where the nav is showing (sm+) rather than centering on its own. */}
      <main id="main-content" tabIndex={-1} className="mx-auto w-full max-w-[1200px] flex-1 px-4 sm:px-[4.5rem] py-6 outline-none">
        <Outlet />
      </main>

      <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t px-4 py-2 text-xs text-muted-foreground print:hidden">
        <nav aria-label="Community resources" className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Link
            to="/about"
            viewTransition={withTransition}
            className="rounded-sm underline underline-offset-2 hover:text-foreground"
          >
            About
          </Link>
          {FOOTER_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className="rounded-sm underline underline-offset-2 hover:text-foreground"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <span>{__APP_VERSION__}</span>
      </footer>
    </div>
  )
}
